import { useCallback, useMemo } from "react";

import {
  type ContentEmailsBoolExp,
  type ContentEmailsQuery,
  useContentEmailsQuery,
} from "src/graphql";

import { brandProfileScopeClause } from "./brand-profile-clause";
import { usePaginatedRows } from "./use-paginated-rows";

const PAGE_SIZE = 40;

type TemplateRow = ContentEmailsQuery["content_emails"][number];

export type TemplateSearchResult = {
  templates: TemplateRow[];
  totalCount: number;
  isLoading: boolean;
  isRefreshing: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
};

export const buildTemplateSearchFilters = (
  search: string,
  brandProfileIds: readonly string[] | null | undefined,
): ContentEmailsBoolExp => {
  // Templates only: content_emails with source = 'standalone_template'.
  const clauses: ContentEmailsBoolExp[] = [
    { source: { _eq: "standalone_template" } },
  ];
  if (search.trim()) {
    clauses.push({ name: { _ilike: `%${search.trim()}%` } });
  }
  const brandProfileClause = brandProfileScopeClause(brandProfileIds);
  if (brandProfileClause) {
    clauses.push(brandProfileClause);
  }
  return { _and: clauses };
};

/**
 * Paginated template search for the picker: name `_ilike` search over the
 * workspace's standalone email templates, optionally scoped to brand
 * profiles. Pagination semantics live in `usePaginatedRows`.
 */
export const useTemplateSearch = ({
  search,
  brandProfileIds,
  enabled = true,
}: {
  search: string;
  /** Only show templates available for one of these brand profiles; null shows every profile's. */
  brandProfileIds?: readonly string[] | null;
  /** Skip the query entirely (e.g. the modal isn't showing this source). */
  enabled?: boolean;
}): TemplateSearchResult => {
  const searchKey = search.trim();

  const filters = useMemo(
    () => buildTemplateSearchFilters(searchKey, brandProfileIds),
    [searchKey, brandProfileIds],
  );

  const { data, isLoading, isFetching } = useContentEmailsQuery(
    {
      filters,
      orderBy: [{ updated_at: "desc" }],
      limit: PAGE_SIZE,
      offset: 0,
    },
    { enabled },
  );

  const fetchPage = useCallback(
    async (offset: number) => {
      const page = await useContentEmailsQuery.fetcher({
        filters,
        orderBy: [{ updated_at: "desc" }],
        limit: PAGE_SIZE,
        offset,
      })();
      return page.content_emails ?? [];
    },
    [filters],
  );

  const { rows, ...pagination } = usePaginatedRows({
    // NUL-delimited so the search string can't collide with the profile id.
    filterKey: `${searchKey}\0${brandProfileIds?.join(",") ?? ""}`,
    firstPage: data?.content_emails ?? [],
    totalCount: data?.content_emails_aggregate?.aggregate?.count ?? 0,
    isLoading,
    isFetching,
    fetchPage,
  });

  return { templates: rows, ...pagination };
};
